<template>
  <v-card
    v-if="visible"
    class="operating-log-editor-bar fill-height right-bar-overlay"
    data-testid="operating-log-editor-bar"
    border
  >
    <v-window
      v-model="visible"
      :mandatory="false"
      eager
    >
      <v-window-item
        key="operatingLog"
        value="operatingLog"
      >
        <v-card
          class="fill-height"
          elevation="2"
          tile
        >
          <v-card-title class="d-flex bb-1 bc-very-light-grey fw-600 pa-5">
            <span tabindex="0">
              {{ $bct(`Edit ${terms.attendance} form`) }}
            </span>
            <v-spacer />

            <v-btn
              @click="close"
              :aria-label="$bct(`Close edit ${terms.attendance}`)"
              class="focus-visible"
              icon="close"
              size="small"
              variant="text"
            />
          </v-card-title>

          <v-card class="bg-very-light-grey ma-3">
            <v-card-text>
              <p class="fs-16 c-grey-70">
                {{ operatingLog?.name }}
              </p>
              <p class="fs-12 c-grey-70">
                {{ `${$bct('Month')}: ${getMonthText(operatingLog?.month, operatingLog?.year)}` }}
              </p>
            </v-card-text>
          </v-card>

          <template v-if="operatingLog">
            <v-card-text class="pa-3">
              <QuestionSet
                v-model="operatingLog"
                @change:attachments="loadAttachments"
                :attachment-owner-id="attachmentOwnerId"
                :attachment-owner-type="'OperatingLog'"
                :attachments="attachments"
                :questions="validPublishedQuestions"
                :readonly="!!operatingLog.submitted_at"
                :schema="schema?.definition"
                expanded
                flat
                hide-actions
                outlined-sections
                paddingless
                very-dense
              />

              <div
                v-for="(question, index) in validVerificationQuestions"
                :key="['verification', index].join('-')"
              >
                <FormQuestion
                  :schema="schema.definition"
                  :subtitle="question.verification_subtitle"
                  :title="question.verification_title"
                  hide-actions
                  very-dense
                >
                  <AttachmentUploader
                    @uploaded="attachments.push($event)"
                    :ref="['uploader', question.id].join('')"
                    :owner="{
                      type: 'OperatingLog',
                      id: operatingLog.id,
                      tag: question.id,
                      tags: [operatingLog.id, question.id],
                    }"
                    class="mb-4"
                  />

                  <AttachmentList
                    @delete="loadAttachments"
                    :attachments="attachments.filter((attachment) => attachment.tag == question.id)"
                    :hide-remove="!!operatingLog.submitted_at"
                    class="mb-6"
                  />
                </FormQuestion>
              </div>
            </v-card-text>
          </template>

          <div class="d-flex justify-space-between pa-4">
            <div>
              <v-btn
                v-if="isSpecialist"
                @click="unsubmitAttendance"
                :disabled="unsubmitButtonDisabled"
                :loading="processing"
                :ripple="false"
                class="focus-very-visible"
                color="primary"
                data-testid="unsubmit-button"
                variant="outlined"
              >
                {{ $bct('Unsubmit') }}
              </v-btn>
            </div>
            <div>
              <v-btn
                @click="close"
                class="focus-very-visible mr-2"
                variant="outlined"
              >
                {{ $bct('Cancel') }}
              </v-btn>
              <v-btn
                @click="save"
                :disabled="readonly"
                :loading="processing"
                class="focus-very-visible mr-2"
                variant="outlined"
              >
                {{ $bct('Save Draft') }}
              </v-btn>
              <v-tooltip
                v-if="submitButtonTooltip"
                :text="submitButtonTooltip"
                location="top"
              >
                <template #activator="{ props: tooltipProps }">
                  <span v-bind="tooltipProps">
                    <v-btn
                      @click="submit"
                      :disabled="!submitButtonEnabled"
                      :loading="processing"
                      :ripple="false"
                      class="focus-very-visible"
                      color="primary"
                      data-testid="submit-button"
                    >
                      {{ readonly ? $bct('Submitted') : $bct('Submit') }}
                    </v-btn>
                  </span>
                </template>
              </v-tooltip>
              <v-btn
                v-else
                @click="submit"
                :disabled="!submitButtonEnabled"
                :loading="processing"
                :ripple="false"
                class="focus-very-visible"
                color="primary"
                data-testid="submit-button"
              >
                {{ readonly ? $bct('Submitted') : $bct('Submit') }}
              </v-btn>
            </div>
          </div>
        </v-card>
      </v-window-item>
    </v-window>
  </v-card>
</template>

<script setup>
import { toRef } from 'vue';
import { useStore } from 'vuex';

import AttachmentList from '@/shared/components/attachments/AttachmentList.vue';
import AttachmentUploader from '@/shared/components/attachments/AttachmentUploader.vue';
import FormQuestion from '@/shared/components/form/FormQuestion.vue';
import QuestionSet from '@/shared/components/form/QuestionSet.vue';
import useApi from '@/shared/composables/useApi';
import useAssets from '@/shared/composables/useAssets';
import useEventBus from '@/shared/composables/useEventBus';
import useQuestionable from '@/shared/composables/useQuestionable';
import useRole from '@/shared/composables/useRole';
import useTerms from '@/shared/composables/useTerms';
import { validateAttendance } from '@/shared/services/attendance-validation';

const $bct = inject('translator');

const eventBus = useEventBus();
const store = useStore();
const { isSpecialist } = useRole();
const { api: Api, scopedApi } = useApi();
const { assets } = useAssets();
const { terms } = useTerms();

const emit = defineEmits(['change']);

const props = defineProps({
  attendanceLogQuestions: {
    type: Array,
    required: true,
  },
  attendanceLogs: {
    type: Array,
    default: () => [],
  },
  schemaId: {
    type: String,
    default: null,
  },
  subsidyProgram: {
    type: Object,
    default: null,
  },
});

const attachments = ref([]);
const operatingLog = ref(null);
const processing = ref(false);
const visible = ref(false);

const { loadQuestions, validVerificationQuestions, validPublishedQuestions } = useQuestionable({
  ownerDataTypeRef: ref('Schema'),
  ownerIdRef: toRef(props, 'schemaId'),
  syncedObjectRef: operatingLog,
});

const attachmentOwnerId = computed(() => {
  return operatingLog.value?.id;
});

const schema = computed(() => {
  const candidateSchema = store.state.schemas[props.schemaId];
  return candidateSchema?.data_type === 'OperatingLog' ? candidateSchema : null;
});

const unsubmitButtonDisabled = computed(() => {
  return !operatingLog.value?.submitted_at;
});

const readonly = computed(() => {
  if (operatingLog.value?.submitted_at) {
    return true;
  }
  return false;
});

const areAttendanceLogsComplete = computed(() => {
  if (!operatingLog.value?.id) return true;
  if (props.attendanceLogs.length === 0) return true;

  const validateDays = props.subsidyProgram?.enable_daily_attendance;
  const errors = validateAttendance(
    props.attendanceLogs,
    props.attendanceLogQuestions,
    validateDays,
  );

  return errors.length === 0;
});

const submitButtonEnabled = computed(() => {
  return !operatingLog.value?.submitted_at && areAttendanceLogsComplete.value;
});

const submitButtonTooltip = computed(() => {
  if (!areAttendanceLogsComplete.value) {
    return 'All attendance logs must be complete before submitting.';
  }
  return null;
});

const getMonthText = (monthValue, year) => {
  if (!monthValue || !year) return '';
  const modifiedMonthValue = assets.month_day_options?.find(
    (option) => option.text === monthValue.toString(),
  )?.value;
  const monthOption = assets.month_options?.find((option) => option.value === modifiedMonthValue);

  return $bct(monthOption?.text) + ' ' + year;
};

function close() {
  visible.value = false;
}

function unsubmitAttendance() {
  if (unsubmitButtonDisabled.value || !isSpecialist) return;

  return unsubmit();
}

async function loadAttachments() {
  const params = {
    owner_type: 'OperatingLog',
    owner_id: operatingLog.value?.id,
  };
  const resp = await scopedApi.attachment.index(params);
  attachments.value = resp.data;
}

async function loadOperatingLog(operatingLogId) {
  const resp = await Api.shared.operatingLog.get(operatingLogId);
  operatingLog.value = resp.data;
}

async function open(operatingLogToEdit) {
  await loadOperatingLog(operatingLogToEdit?.id);
  await loadAttachments();
  await loadQuestions();

  visible.value = 'operatingLog';
}

async function save() {
  processing.value = true;
  const params = { custom: operatingLog.value.custom };
  const resp = await Api.shared.operatingLog.update(operatingLog.value?.id, params);
  processing.value = false;
  if (resp?.status !== 200) return false;

  eventBus.chime('Saved');
  emit('change', resp.data);
  close();
  return true;
}

async function submit() {
  processing.value = true;
  const params = { submitted: true, custom: operatingLog.value?.custom };
  const resp = await Api.shared.operatingLog.update(operatingLog.value?.id, params);
  processing.value = false;
  if (resp?.status !== 200) return false;

  eventBus.chime('Submitted');
  operatingLog.value = resp.data;
  close();
  emit('change', operatingLog.value);
  return true;
}

async function unsubmit() {
  processing.value = true;
  const params = { submitted: false };
  const resp = await Api.shared.operatingLog.update(operatingLog.value?.id, params);
  processing.value = false;
  if (resp?.status !== 200) return false;

  eventBus.chime('Unsubmitted');
  operatingLog.value = resp.data;
  emit('change', operatingLog.value);
  return true;
}

defineExpose({
  open,
  close,
});
</script>

<style scoped>
.operating-log-editor-bar {
  overflow-y: auto;
  padding-top: 64px;
  position: fixed;
  top: 0;
  z-index: 1000;
  right: 0;
  width: 30%;
}

@media only screen and (max-width: 1200px) {
  .operating-log-editor-bar {
    overflow-y: auto;
    padding-top: 64px;
    padding-bottom: 72px;
    position: fixed;
    top: 0;
    z-index: 1000;
    right: 0;
    width: 50%;
  }
}

@media only screen and (max-width: 800px) {
  .operating-log-editor-bar {
    overflow-y: auto;
    padding-top: 64px;
    padding-bottom: 72px;
    position: fixed;
    top: 0;
    z-index: 1000;
    right: 0;
    width: 100%;
  }
}
</style>
